import { Badge, Card } from '@yoast/ui-library';

import { useGetAccountDebug } from '../../api/generated/insights';
import CardHeader from '../../components/CardHeader';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString();
}

const AccountDebug = () => {
  const { data, isLoading, error } = useGetAccountDebug({
    query: {
      select: ({ data }) => data,
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Account debug" />
        <Card.Content>
          <p className="yst-text-slate-500">Loading...</p>
        </Card.Content>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader title="Account debug" />
        <Card.Content>
          <p className="yst-text-red-600">
            Failed to load account debug info.
          </p>
        </Card.Content>
      </Card>
    );
  }

  return (
    <div className="yst-space-y-6">
      {/* MyYoast info */}
      <Card>
        <CardHeader
          title="MyYoast subscription info"
          description="What MyYoast reports about your account."
        />
        <Card.Content>
          <table className="yst-w-full yst-text-sm">
            <tbody>
              <tr className="yst-border-b yst-border-slate-200">
                <td className="yst-py-3 yst-pr-4 yst-font-medium yst-text-slate-700">
                  Weekly analysis limit
                </td>
                <td className="yst-py-3">
                  {data.myYoast?.weeklyLimit ?? '-'}
                </td>
              </tr>
              <tr className="yst-border-b yst-border-slate-200">
                <td className="yst-py-3 yst-pr-4 yst-font-medium yst-text-slate-700">
                  Eligible for free trial (MyYoast)
                </td>
                <td className="yst-py-3">
                  <Badge
                    variant={
                      data.myYoast?.isEligibleForFreeTrial ? 'info' : 'plain'
                    }
                  >
                    {data.myYoast?.isEligibleForFreeTrial ? 'Yes' : 'No'}
                  </Badge>
                </td>
              </tr>
              <tr>
                <td className="yst-py-3 yst-pr-4 yst-font-medium yst-text-slate-700">
                  Eligible for trial analysis (AIBI)
                </td>
                <td className="yst-py-3">
                  <Badge
                    variant={
                      data.myYoast?.isEligibleForTrialAnalysis
                        ? 'info'
                        : 'plain'
                    }
                  >
                    {data.myYoast?.isEligibleForTrialAnalysis ? 'Yes' : 'No'}
                  </Badge>
                </td>
              </tr>
            </tbody>
          </table>
        </Card.Content>
      </Card>

      {/* Brands with their analyses */}
      {!data.brands?.length ? (
        <Card>
          <CardHeader title="Brands" />
          <Card.Content>
            <p className="yst-text-slate-500">No brands found.</p>
          </Card.Content>
        </Card>
      ) : (
        data.brands.map((brand) => {
          const brandAnalyses =
            data.analyses?.filter((a) => a.brandId === brand.id) ?? [];
          return (
            <Card key={brand.id}>
              <CardHeader
                title={
                  <span className="yst-flex yst-items-center yst-gap-3">
                    {brand.name}
                    {brand.deletedAt ? (
                      <Badge variant="plain">
                        Deleted {formatDate(brand.deletedAt)}
                      </Badge>
                    ) : (
                      <Badge variant="info">Active</Badge>
                    )}
                  </span>
                }
                description={`Created ${formatDate(brand.createdAt)}`}
              />
              <Card.Content>
                {brandAnalyses.length === 0 ? (
                  <p className="yst-text-slate-500">
                    No analyses for this brand.
                  </p>
                ) : (
                  <div className="yst-overflow-x-auto">
                    <table className="yst-w-full yst-text-sm">
                      <thead>
                        <tr className="yst-border-b yst-border-slate-300 yst-text-left">
                          <th className="yst-py-2 yst-pr-4 yst-font-medium yst-text-slate-700">
                            Created
                          </th>
                          <th className="yst-py-2 yst-pr-4 yst-font-medium yst-text-slate-700">
                            Finished
                          </th>
                          <th className="yst-py-2 yst-pr-4 yst-font-medium yst-text-slate-700">
                            Trial
                          </th>
                          <th className="yst-py-2 yst-font-medium yst-text-slate-700">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandAnalyses.map((analysis) => (
                          <tr
                            key={analysis.id}
                            className="yst-border-b yst-border-slate-200"
                          >
                            <td className="yst-py-2 yst-pr-4">
                              {formatDate(analysis.createdAt)}
                            </td>
                            <td className="yst-py-2 yst-pr-4">
                              {formatDate(analysis.finishedAt)}
                            </td>
                            <td className="yst-py-2 yst-pr-4">
                              {analysis.isTrial ? 'Yes' : '-'}
                            </td>
                            <td className="yst-py-2">
                              {analysis.deletedAt ? (
                                <Badge variant="plain">
                                  Deleted {formatDate(analysis.deletedAt)}
                                </Badge>
                              ) : analysis.finishedAt ? (
                                <Badge variant="info">Completed</Badge>
                              ) : (
                                <Badge variant="plain">In progress</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Content>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default AccountDebug;
